import { Characteristics } from '@workspace/ui/components/Characteristics';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { truncate } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RecommendationSource } from '~/api/Catalog';
import { company } from '~/company/Company';
import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import CollapsibleText from '~/components/CollapsibleText/CollapsibleText';
import {
  type BreadcrumbItem,
  Breadcrumbs,
} from '~/components/isolated/Breadcrumbs';
import { Button, SIZE } from '~/components/isolated/Button';
import { useGlobal } from '~/hooks/useGlobal';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import NotFoundV3 from '~/pages/NotFoundV3';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore } from '~/stores';
import { filterNutritionFields, parseNutrition } from '~/types/Product';
import type { ProductViewProps } from '../../Product';
import { AddToCardButton } from './components/AddToCardButton';
import { ListOfItems } from './components/ListOfItems';
import { MiscButtons } from './components/MiscButtons';
import { ProductBadges } from './components/ProductBadges';
import { ProductImageGallery } from './components/ProductImageGallery';
import ProductMultibuyLevels from './components/ProductMultibuyLevels';
import { ProductPrice } from './components/ProductPrice';
import { ProductProperties } from './components/ProductProperties';
import { ProductRatingSummary } from './components/ProductRatingSummary';
import type { RatingData } from './components/ProductRatingSummary/interfaces';
import { ProductRecommendations } from './components/ProductRecommendations';
import ProductRecommendationsGridXL from './components/ProductRecommendations/ProductRecommendationsGridXL';
import { ProductReviews } from './components/ProductReviews';
import { ProductTitle } from './components/ProductTitle';
import { Tabs } from './components/Tabs';
import type { TabItem } from './components/Tabs/interfaces';
import { PRODUCT_TABS } from './constants';
import styles from './ProductV3.module.scss';

const ProductV3 = ({
  currentItem,
  product,
  isLoading,
  isBundle,
  bundleImages,
  title,
  isExpress,
  handleChangeOffer,
  handleChangeCount,
  count,
  limit,
  propertyList,
  badgeList,
  handleFavorite,
  isMultiBuy,
  currentPrice,
}: ProductViewProps) => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isMCosmetic = company.is('mcosmetic');

  const compositionProperty = useMemo(
    () =>
      isMCosmetic ? propertyList.find((p) => p.code === 'sostav') : undefined,
    [propertyList, isMCosmetic],
  );

  const applicationProperty = useMemo(
    () =>
      isMCosmetic
        ? propertyList.find((p) => p.code === 'primenenie')
        : undefined,
    [propertyList, isMCosmetic],
  );

  const characteristicsList = useMemo(() => {
    const characteristics = filterNutritionFields(propertyList).filter(
      (p) =>
        !(
          isMCosmetic &&
          ['sostav', 'primenenie', 'kod_upakovki', 'ikpu'].includes(p.code)
        ),
    );
    if (company.isMarketplaceVariant) {
      return characteristics.filter(
        (property) =>
          property.code !== 'ozon_link' &&
          property.code !== 'ozon_reviews_link' &&
          property.code !== 'ozon_q_a_link',
      );
    }
    return characteristics;
  }, [propertyList, company.isMarketplaceVariant]);

  const nutritionList = useMemo(() => {
    const items = parseNutrition(propertyList);
    const SUB_ITEM_MAP: Record<string, string> = {
      fat: 'fat_saturates',
      carbohydrates: 'sugar',
    };
    const subItemCodes = new Set(Object.values(SUB_ITEM_MAP));

    const formatValue = (val: string, additionalVal: string | undefined) =>
      additionalVal ? `${additionalVal}\n${val}` : val;

    return items
      .filter((item) => !subItemCodes.has(item.name))
      .map((item) => {
        const subItemCode = SUB_ITEM_MAP[item.name];
        const subItem = subItemCode
          ? items.find((i) => i.name === subItemCode)
          : undefined;

        return {
          code: item.name,
          name: t(`productProperties:${item.name}`),
          value: formatValue(item.value, item.additionalValue),
          extraValue: item.ratio,
          ...(subItem && {
            subItems: [
              {
                name: t(`nutritionTableNames:${subItem.name}`),
                value: formatValue(subItem.value, subItem.additionalValue),
                extraValue: subItem.ratio,
              },
            ],
          }),
        };
      });
  }, [propertyList, t]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const result: BreadcrumbItem[] = [
      { label: t('home'), link: '/' },
      { label: t('allCategories'), link: '/shop' },
    ];

    if (product.data?.categoryId) {
      const { categoryName, categorySlug } = product.data;
      result.push({
        label: truncate(categoryName, { length: 25 }),
        link: createCategoryUrl({ id: categorySlug }),
      });
    }

    if (product.data?.name) {
      result.push({
        label: truncate(product.data.name, { length: 80 }),
      });
    }

    return result;
  }, [product.data, t]);

  useStatusBarControl({
    initialColor: 'light-content',
    scrolledColor: 'dark-content',
    scrollThreshold: 40,
  });

  const pageTitle =
    product.selectedOffer?.metadataTitle ||
    product.data?.metadataTitle ||
    product.data?.name ||
    '';
  const pageDescription =
    product.selectedOffer?.metadataDescription ||
    product.data?.metadataDescription ||
    '';
  const canonical = company.config.canonical + (pathname ?? '');

  const loaderActive = product.request.isLoading || isLoading;

  const ratingData = (() => {
    if (!company.useReviewsOnProductPage) {
      return null;
    }

    const reviews = product.reviews.list;
    if (!reviews.length) {
      return null;
    }

    const totalReviews = product.reviews.request.total || reviews.length;
    const ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    for (const review of reviews) {
      const mark = Math.min(
        5,
        Math.max(1, Math.round(review.mark)),
      ) as keyof typeof ratingCounts;
      ratingCounts[mark]++;
    }

    const averageRating = Number(
      (reviews.reduce((sum, r) => sum + r.mark, 0) / reviews.length).toFixed(1),
    );

    const toPercentage = (count: number) =>
      totalReviews > 0 ? Number(((count / totalReviews) * 100).toFixed(1)) : 0;

    return {
      averageRating,
      totalReviews,
      distribution: ([5, 4, 3, 2, 1] as const).map((stars) => ({
        stars,
        count: ratingCounts[stars] ?? 0,
        percentage: toPercentage(ratingCounts[stars] ?? 0),
      })),
    } satisfies RatingData;
  })();

  if (product.notFound) {
    return <NotFoundV3 />;
  }

  const ozonLinkByType = {
    page: 'ozon_link',
    reviews: 'ozon_reviews_link',
    questions: 'ozon_q_a_link',
  } as const;

  const getOzonUrl = (type: keyof typeof ozonLinkByType = 'page') => {
    const property = propertyList.find(
      (item) => item.code === ozonLinkByType[type],
    );
    return property?.value.toString() ?? 'https://ozon.ru';
  };

  const productTabs: TabItem[] = [
    isBundle
      ? {
          id: PRODUCT_TABS.listOfItems,
          content: <ListOfItems product={product} />,
        }
      : null,

    nutritionList.length > 0 ||
    badgeList.length > 0 ||
    characteristicsList.length > 0
      ? {
          id: PRODUCT_TABS.characteristics,
          content: (
            <div>
              {badgeList.length > 0 && (
                <Characteristics
                  propertyList={[]}
                  badgeList={badgeList}
                  isMarketplace={company.useMarketplaceProductLayout}
                  showHeader={false}
                  labels={{
                    characteristic: t('characteristic'),
                    characteristicValue: t('characteristicValue'),
                    seeAll: t('seeAll'),
                    showLess: t('showLess'),
                  }}
                />
              )}
              {characteristicsList.length > 0 && (
                <>
                  <Typography.Text200Semibold
                    as="p"
                    className="text-text-primary"
                  >
                    {t('productInformation')}
                  </Typography.Text200Semibold>
                  <Characteristics
                    propertyList={characteristicsList}
                    isMarketplace={company.useMarketplaceProductLayout}
                    showHeader={false}
                    borderless
                    labels={{
                      characteristic: t('characteristic'),
                      characteristicValue: t('characteristicValue'),
                      seeAll: t('seeAll'),
                      showLess: t('showLess'),
                    }}
                  />
                </>
              )}
              {nutritionList.length > 0 && (
                <>
                  <Typography.Text200Semibold
                    as="p"
                    className="text-text-primary"
                  >
                    {t('nutritionalInformation')}
                  </Typography.Text200Semibold>
                  <Characteristics
                    propertyList={nutritionList}
                    isMarketplace={company.useMarketplaceProductLayout}
                    labels={{
                      characteristic: t('characteristic'),
                      characteristicValue: t('characteristicValue'),
                      extraHeader: t('productPage:riPer100g'),
                      seeAll: t('seeAll'),
                      showLess: t('showLess'),
                    }}
                  />
                </>
              )}
            </div>
          ),
        }
      : null,
    currentItem?.description
      ? {
          id: PRODUCT_TABS.description,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={currentItem.description}
              isLoading={product.request.isLoading}
              includeIcon={false}
              scrollToTopOnCollapse={company.useMarketplaceProductLayout}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
    applicationProperty
      ? {
          id: PRODUCT_TABS.application,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={String(applicationProperty.value)}
              includeIcon={false}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
    compositionProperty
      ? {
          id: PRODUCT_TABS.composition,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={String(compositionProperty.value)}
              includeIcon={false}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
  ].filter(Boolean) as TabItem[];

  return (
    <>
      <Helmet>
        <title>
          {pageTitle && `${pageTitle} - `}
          {company.config.name}
        </title>
        <meta name="description" content={pageDescription} />
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <link data-react-helmet="true" rel="canonical" href={canonical} />
      </Helmet>

      <div className={cn(styles.root, mainStore.isRN && styles.addAppGap)}>
        <div
          className={cn(
            styles.container,
            company.useMarketplaceProductLayout && styles.containerMarketplace,
          )}
        >
          <Breadcrumbs
            items={breadcrumbItems}
            isLoading={loaderActive}
            className={styles.breadcrumbs}
          />

          <div className={styles.product}>
            <section>
              <ProductImageGallery
                isBundle={isBundle}
                bundleImages={bundleImages}
                currentItem={currentItem}
                product={product}
                handleFavorite={handleFavorite}
                isLoading={loaderActive}
              />
            </section>
            <section className={styles.info}>
              <ProductTitle isLoading={loaderActive}>{title}</ProductTitle>
              <ProductBadges
                isBundle={isBundle}
                currentItem={currentItem}
                isExpress={isExpress}
                className={styles.badges}
                isLoading={loaderActive}
              />
              <ProductPrice
                currentItem={currentItem}
                currentPrice={currentPrice}
                isMultiBuy={isMultiBuy}
                isLoading={loaderActive}
                count={count}
              />
              <ProductMultibuyLevels
                currentItem={currentItem}
                isMultiBuy={isMultiBuy}
                isLoading={loaderActive}
                count={count}
              />
              {!isBundle && (
                <ProductProperties
                  className={styles.volumes}
                  product={product}
                  handleChangeOffer={handleChangeOffer}
                  isLoading={loaderActive}
                />
              )}
              {!isMobile &&
                (company.useMarketplaceProductLayout ? (
                  <div className={styles.ctaArea}>
                    <Button
                      size={SIZE.XL}
                      className={styles.buttonMarketplace}
                      onClick={() => {
                        analyticsService.track(AnalyticsEvent.CLICK_BUY, {
                          external_id: product.data?.id,
                          sku: currentItem?.sku,
                        });
                        window.open(getOzonUrl(), '_blank');
                      }}
                    >
                      {t('buyOnOzon')}
                    </Button>
                  </div>
                ) : (
                  <div className={styles.ctaArea}>
                    <AddToCardButton
                      handleChangeCount={handleChangeCount}
                      count={count}
                      limit={limit}
                      isLoading={loaderActive}
                      product={product}
                      currentItem={currentItem}
                      isBundle={isBundle}
                      isMultiBuy={isMultiBuy}
                    />
                    <MiscButtons
                      product={product}
                      handleFavorite={handleFavorite}
                      isLoading={loaderActive}
                    />
                  </div>
                ))}
              <Tabs
                key={product.data?.id}
                tabs={productTabs}
                defaultValue={
                  isBundle ? PRODUCT_TABS.listOfItems : PRODUCT_TABS.description
                }
                isLoading={loaderActive}
                className={styles.tabs}
                tabsClassName="overscreen-4 overflow-x-auto overflow-y-hidden max-md:max-w-screen scrollbar-hide"
              />
            </section>
          </div>

          {company.useReviewsOnProductPage && product.reviews && ratingData && (
            <div className={styles.reviewSection}>
              <div className={styles.reviewColumn}>
                <ProductReviews
                  reviews={product.reviews.list}
                  totalCount={
                    product.reviews.request.total || product.reviews.list.length
                  }
                  ozonUrl={getOzonUrl('reviews')}
                  onLoadMore={product.reviews.request.loadMore}
                  canLoadMore={product.reviews.request.canLoadMore}
                />
              </div>
              {ratingData && (
                <div className={styles.ratingColumn}>
                  <ProductRatingSummary
                    rating={ratingData.averageRating}
                    ratingData={ratingData}
                  />
                </div>
              )}
            </div>
          )}
          {company.useMarketplaceProductLayout ? (
            <ProductRecommendationsGridXL
              title="similarProducts"
              source={RecommendationSource.CARD_SIMILAR}
              isLoading={loaderActive}
            />
          ) : (
            <ProductRecommendations
              title="similarProducts"
              source={RecommendationSource.CARD_SIMILAR}
              isLoading={loaderActive}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default observer(ProductV3);
